import styled, { keyframes } from "styled-components";

const skeletonPulse = keyframes`
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
`;

export const StyledCasinoLobbyPage = styled.div`
  .casino-lobby-shell {
    display: flex;
    flex-direction: column;
    gap: 22px;
    width: 100%;
    padding: 8px 0 46px;
  }

  .casino-lobby-search-row {
    display: flex;
    width: 100%;
  }

  .casino-lobby-search-box {
    position: relative;
    display: flex;
    align-items: center;
    width: min(100%, 1100px);
    height: 48px;
    border-radius: 8px;
    border: 1px solid var(--crypto-border);
    background: rgba(7, 19, 27, 0.58);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
    transition: border-color 0.18s ease, box-shadow 0.18s ease;
  }

  .casino-lobby-search-box:focus-within {
    border-color: rgba(89, 244, 119, 0.7);
    box-shadow: 0 0 0 3px rgba(89, 244, 119, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  }

  .casino-search-icon {
    position: relative;
    flex: 0 0 auto;
    width: 17px;
    height: 17px;
    margin-left: 18px;
    border: 2px solid #9cb2c4;
    border-radius: 50%;
    opacity: 0.9;
  }

  .casino-search-icon::after {
    content: "";
    position: absolute;
    width: 8px;
    height: 2px;
    right: -6px;
    bottom: -3px;
    border-radius: 999px;
    background: #9cb2c4;
    transform: rotate(45deg);
  }

  .casino-lobby-search-box input {
    width: 100%;
    min-width: 0;
    height: 100%;
    border: 0;
    outline: 0;
    color: #e9f2f5;
    background: transparent;
    padding: 0 18px 0 15px;
    font-size: 15px;
    font-weight: 500;
  }

  .casino-lobby-search-box input::placeholder {
    color: #86a0b4;
  }

  .casino-lobby-toolbar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
  }

  .casino-lobby-title h1 {
    margin: 0;
    color: #ffffff;
    font-size: 20px;
    line-height: 1.2;
    font-weight: 900;
  }

  .casino-lobby-title span {
    display: inline-flex;
    margin-top: 5px;
    color: #8fa1b0;
    font-size: 13px;
    font-weight: 500;
  }

  .casino-sort-control {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #a8b8c7;
    font-size: 13px;
    font-weight: 700;
  }

  .casino-sort-control select {
    height: 38px;
    min-width: 104px;
    border: 0;
    border-radius: 8px;
    color: #ffffff;
    background: var(--crypto-surface-raised);
    padding: 0 12px;
    font-size: 13px;
    font-weight: 700;
    outline: 0;
  }

  .casino-lobby-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, 150px);
    justify-content: start;
    gap: 24px 12px;
    align-items: start;
  }

  .casino-lobby-card-link {
    display: block;
    width: 150px;
    border: 0;
    background: transparent;
    padding: 0;
    color: inherit;
    text-align: inherit;
    font: inherit;
    cursor: pointer;
  }

  .casino-lobby-card {
    position: relative;
    width: 150px;
    aspect-ratio: 5 / 7;
    overflow: hidden;
    border-radius: 8px;
    background:
      linear-gradient(160deg, rgba(89, 244, 119, 0.1), rgba(12, 32, 40, 0.88)),
      var(--crypto-surface-2);
    border: 0;
    box-shadow: 0 10px 18px rgba(0, 0, 0, 0.18);
    transform: translateY(0);
    transition: transform 0.24s ease, box-shadow 0.24s ease;
  }

  .casino-lobby-card-link:hover .casino-lobby-card,
  .casino-lobby-card-link:focus-visible .casino-lobby-card {
    transform: translateY(-9px);
    box-shadow: 0 20px 34px rgba(0, 0, 0, 0.34), 0 0 20px rgba(89, 244, 119, 0.09);
  }

  .casino-lobby-card img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    background: var(--crypto-surface-3);
    transform: scale(1.01);
    transition: transform 0.28s ease, filter 0.28s ease;
  }

  .casino-lobby-card-link:hover .casino-lobby-card img,
  .casino-lobby-card-link:focus-visible .casino-lobby-card img {
    filter: blur(2px) brightness(0.58) saturate(1.12);
    transform: scale(1.07);
  }

  .casino-lobby-card.no-image {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .casino-lobby-card.no-image::before {
    content: "";
    position: absolute;
    inset: 13px;
    border-radius: 7px;
    border: 1px solid rgba(89, 244, 119, 0.14);
    background:
      radial-gradient(circle at 22% 18%, rgba(89, 244, 119, 0.2), transparent 32%),
      linear-gradient(135deg, rgba(255, 255, 255, 0.06), transparent);
  }

  .casino-lobby-card::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(9, 12, 18, 0.02) 22%, rgba(9, 12, 18, 0.18) 52%, rgba(6, 8, 12, 0.88) 100%);
    pointer-events: none;
    transition: opacity 0.24s ease;
  }

  .casino-lobby-card-link:hover .casino-lobby-card::after,
  .casino-lobby-card-link:focus-visible .casino-lobby-card::after {
    opacity: 0.9;
  }

  .casino-lobby-card-title {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 22px;
    z-index: 2;
    padding: 0 12px;
    display: flex;
    justify-content: center;
    text-align: center;
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .casino-lobby-card-title strong,
  .casino-lobby-card-hover strong {
    color: #ffffff;
    font-family: var(--crypto-display-font);
    font-size: 17px;
    line-height: 1.16;
    font-weight: 700;
    letter-spacing: 0;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.78), 0 0 14px rgba(255, 255, 255, 0.08);
    word-break: keep-all;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .no-image .casino-lobby-card-title {
    inset: 0;
    bottom: auto;
    align-items: center;
    padding: 16px;
  }

  .casino-lobby-card-link:hover .casino-lobby-card-title,
  .casino-lobby-card-link:focus-visible .casino-lobby-card-title {
    opacity: 0;
    transform: translateY(10px);
  }

  .casino-lobby-card-hover {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 26px;
    padding: 22px 13px 24px;
    text-align: center;
    background: rgba(8, 12, 17, 0.18);
    opacity: 0;
    transform: translateY(10px);
    transition: opacity 0.22s ease, transform 0.22s ease;
  }

  .casino-lobby-card-link:hover .casino-lobby-card-hover,
  .casino-lobby-card-link:focus-visible .casino-lobby-card-hover {
    opacity: 1;
    transform: translateY(0);
  }

  .casino-lobby-play {
    width: 58px;
    height: 58px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #59f477, #16b857);
    box-shadow:
      0 14px 28px rgba(22, 184, 87, 0.42),
      0 0 24px rgba(89, 244, 119, 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 0.44);
  }

  .casino-lobby-play::before {
    content: "";
    width: 0;
    height: 0;
    margin-left: 4px;
    border-top: 11px solid transparent;
    border-bottom: 11px solid transparent;
    border-left: 16px solid #ffffff;
    filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.2));
  }

  .casino-lobby-skeleton {
    width: 150px;
    aspect-ratio: 5 / 7;
    border-radius: 8px;
    background: linear-gradient(90deg, rgba(26, 37, 53, 0.88), rgba(43, 58, 76, 0.88), rgba(26, 37, 53, 0.88));
    background-size: 220% 100%;
    animation: ${skeletonPulse} 1.2s ease-in-out infinite;
  }

  .casino-lobby-state {
    min-height: 110px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a7b8;
    background: rgba(24, 31, 46, 0.64);
    border: 1px solid rgba(139, 177, 205, 0.14);
    font-size: 14px;
    font-weight: 600;
  }

  .casino-lobby-state.error {
    color: #ffb0b0;
    border-color: rgba(255, 112, 112, 0.28);
    background: rgba(58, 22, 27, 0.32);
  }

  @media (max-width: 700px) {
    .casino-lobby-shell {
      gap: 18px;
      padding-bottom: 34px;
    }

    .casino-lobby-search-box {
      height: 44px;
    }

    .casino-lobby-toolbar {
      align-items: flex-start;
      flex-direction: column;
    }

    .casino-sort-control {
      width: 100%;
      justify-content: space-between;
    }

    .casino-sort-control select {
      min-width: 116px;
    }

    .casino-lobby-grid {
      grid-template-columns: repeat(auto-fill, minmax(136px, 1fr));
      gap: 18px 10px;
    }

    .casino-lobby-card-link,
    .casino-lobby-card,
    .casino-lobby-skeleton {
      width: 100%;
      max-width: 150px;
      justify-self: center;
    }

    .casino-lobby-card-title strong,
    .casino-lobby-card-hover strong {
      font-size: 15px;
    }
  }
`;
